import React, { useState } from 'react';
import { X, KeyRound, User, Mail, ShieldCheck, AlertCircle, CheckCircle } from 'lucide-react';
import { AuthService } from '../services/authService';
import { ROLE_LABELS } from '../utils/constants';

// Modal « Mon compte » : informations du compte connecté + changement de mot de passe.
// Accessible à tous les rôles (y compris les techniciens dont le compte a été créé par le cabinet).
const AccountSettings = ({ user, role, onClose }) => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const userData = user?.userData || {};
  const displayName = userData.agentName || userData.username || user?.displayName || '';

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.current) {
      setError('Saisissez votre mot de passe actuel');
      return;
    }
    if (form.next.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (form.next !== form.confirm) {
      setError('Les deux nouveaux mots de passe ne correspondent pas');
      return;
    }
    if (form.next === form.current) {
      setError('Le nouveau mot de passe doit être différent de l\'actuel');
      return;
    }

    setSaving(true);
    const result = await AuthService.changePassword(form.current, form.next);
    setSaving(false);

    if (result.success) {
      setSuccess('Mot de passe modifié avec succès');
      setForm({ current: '', next: '', confirm: '' });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="modal-scrim" style={{ zIndex: 9999 }}>
      <div className="modal-panel max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-slate-900 flex items-center">
            <KeyRound className="h-5 w-5 mr-2 text-brand-700" />
            Mon compte
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex items-center text-slate-700">
              <User className="h-4 w-4 mr-2 text-slate-400" />
              <span className="font-medium">{displayName || '-'}</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Mail className="h-4 w-4 mr-2 text-slate-400" />
              <span>{user?.email}</span>
            </div>
            <div className="flex items-center text-slate-700">
              <ShieldCheck className="h-4 w-4 mr-2 text-slate-400" />
              <span>{ROLE_LABELS[role] || role}</span>
            </div>
          </div>

          {/* Changement de mot de passe */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Changer le mot de passe</h3>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {success}
              </div>
            )}

            <input
              type="password"
              value={form.current}
              onChange={(e) => handleChange('current', e.target.value)}
              autoComplete="current-password"
              placeholder="Mot de passe actuel"
              className="field"
            />
            <input
              type="password"
              value={form.next}
              onChange={(e) => handleChange('next', e.target.value)}
              autoComplete="new-password"
              placeholder="Nouveau mot de passe (6 caractères min.)"
              className="field"
            />
            <input
              type="password"
              value={form.confirm}
              onChange={(e) => handleChange('confirm', e.target.value)}
              autoComplete="new-password"
              placeholder="Confirmer le nouveau mot de passe"
              className="field"
            />

            <button
              type="submit"
              disabled={saving}
              className="btn-primary w-full"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {saving ? 'Modification...' : 'Modifier le mot de passe'}
            </button>
          </form>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50">
          <button
            onClick={onClose}
            className="btn-secondary w-full"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
